import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { Context } from "../redux/Context";

/**
 * Subscribes to a single SignalR event for as long as the calling component is mounted.
 *
 * Replaces the hand-rolled `useEffect` + `if (hubConnection)` + `hubConnection.on(...)` +
 * `return () => hubConnection.off(name)` block that all eight consumers used to carry. Three
 * things here are deliberate, and they are the whole point of centralising this:
 *
 *   1. Unsubscribing passes the handler instance: `off(name, listener)`. `off(name)` on its own
 *      removes EVERY handler registered for that name, app-wide, which is what the old cleanups
 *      did. ReceiveBidStatus has four subscribers, so one of them unmounting used to deafen the
 *      other three until the next reconnect happened to rebuild them.
 *   2. The caller's handler is held in a ref, so the subscription depends only on the connection
 *      and the event name. Callers can pass a fresh inline arrow on every render - the natural
 *      way to write one - without the subscription being torn down and rebuilt. Two effects used
 *      to re-register on every inbound message because their own handlers set the state they
 *      depended on, leaving a window with nothing attached in which messages were dropped.
 *   3. hubConnection stays in the dependency array. Since #182 ContextProvider keeps ONE
 *      HubConnection per sign-in and restarts that same object after a drop, so handlers survive a
 *      reconnect by themselves; a new sign-in publishes a new connection, and re-subscribing when its
 *      identity changes is how handlers move to it - not an oversight. Messages missed while it was
 *      down are not replayed: see useHubResync.
 *
 * The ref is refreshed in a layout effect rather than a passive one to keep the window in which
 * an arriving message would still reach the previous render's handler as small as React allows.
 * There is no SSR here (Create React App, browser only), so useLayoutEffect is safe.
 *
 * @param {string} eventName Server-to-client method name, e.g. "ReceiveBidStatus". Must match a
 *   member of the hub's IMessage contract.
 * @param {(...args: any[]) => void} handler Called with the server's arguments. May be a new
 *   function on every render; the latest one is always what runs.
 * @returns {void}
 */
export const useHubEvent = (eventName, handler) => {
  const { hubConnection } = useContext(Context);
  const handlerRef = useRef(handler);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    // hubConnection is the number 0 until ContextProvider's first connection resolves.
    if (!hubConnection || !eventName) {
      return undefined;
    }

    const listener = (...args) => handlerRef.current?.(...args);

    hubConnection.on(eventName, listener);
    return () => {
      hubConnection.off(eventName, listener);
    };
  }, [hubConnection, eventName]);
};
